<template>
  <div v-if="event && race">
    <v-breadcrumbs v-if="!isEmbedded" :items="breadcrumbItems"/>
    <v-card :elevation="isEmbedded ? 0 : 2">
      <EventHeader v-if="!isEmbedded" :event="event" :title="race.name"/>
      <EventFriendReferral :event="event" :race="race"/>
      <RaceResultsVideos ref="videoResults" v-if="isWorkoutVideoChallenge" :event="event" :race="race" :meta="meta" :badges="badges" />
      <v-card-title v-if="race.route || true" class="headline">
        <span class="mr-2">{{$t('events.race.leaderboard')}}</span>
        <span class="mr-2">{{ race.name }}</span>
        <span v-if="race && !race.hide_totals && meta && meta.total_count" class="" style="font-size:70%;">({{meta.total_count}} {{$t('events.race.results-label')}})</span>
      </v-card-title>
      <v-card-text v-if="user && race.badge_scoring === 'MANUAL_CLAIM' && !race.team">
        <v-btn large outlined :color="event.color || 'primary'" :to="{name: 'activityClaim', params: {eventId: event.id, raceId: race.id}}">{{ $t('profile.activities.entry.claim-achievement') }}</v-btn>
      </v-card-text>
      <RaceResultFilter :meta="meta" :event="event" :race="race" :title="$t('events.race.title')" :filter="resultFilter" :showRace="!isEmbedded" @change="updateResults" ref="filter"/>
      <vue-markdown v-if="race.result_desc" class="markdown mx-4 mb-4" :html="false" :source="race.result_desc" />
      <RaceResultsMapOptions ref="resultMap" :event="event" :race="race" />
      <RaceResultsMap ref="resultMap" :event="event" :race="race" :badges="badges" :meta="meta" @showResultDetails="openResultPopup" @showBadgeDetails="openBadgeDetails"/>
      <RaceResultsTileMap ref="tileResults" v-if="tiles" :event="event" :race="race" :meta="meta" :badges="badges" @showresults="loadSubResults" />
      <v-alert v-if="race.geofence" type="info" tile>
        {{$t('events.rules.geofence')}}
        <RaceGeoFenceDialog :event="event" :race="race" color="white" class="float-right" />
      </v-alert>      

      <div v-if="meta && meta.histogram" class="">
        <v-card-title class="subtitle px-3">
          {{ $t('results.details.finish-time-breakdown') }}
        </v-card-title>
        <v-card-text v-if="meta.median_score">{{meta.median_score | time }} is the most common (median) finish time. This is when most participants were finishing.</v-card-text>
        <RaceHistogram :data="meta.histogram" :medianMinute="meta.median_score ? Math.floor(meta.median_score / 60) : null" minimal />
      </div>
      <div v-if="meta && meta.score_by_date" class="">
        <v-card-title class="subtitle px-3">
          {{ scoreByDayLabel }}
        </v-card-title>
        <RaceHistogramByDate :data="meta.score_by_date" :label="scoreByDayLabel" :defaultColor="event.color || $vuetify.theme.defaults.light.primary"  :accentColor="$helpers.getColorVariant(event.color || $vuetify.theme.defaults.light.primary, 100)" />
      </div>
      <v-alert v-if="race.personal_goal" class="grey lighten-4 black--text" tile>{{$t('events.rules.personal-goal')}}</v-alert>      
      <v-alert v-if="(race.top_x || race.top_x === 0) && race.visibility !== 'PRIVATE'" class="grey lighten-4 black--text" tile>{{$t('results.top-x-only', {limit: race.top_x})}}</v-alert>      

      <div v-if="subResults">
        <v-card-title class="sutitle">
          <a @click="loadSubResults(null)">All Results</a>
           <span class="mx-2">&raquo;</span>
          Results for {{subResults.title}}
           <v-btn v-if="false" text small @click="subResults=null">Show All</v-btn>
        </v-card-title>
        <RaceResultsGrid :event="event" :race="race" :results="subResults.results" :meta="meta" :badges="badges" :fixedColumns="subResults.columns" showSubResults @detailsClose="updateMetaTags" />
      </div>
      <div v-if="race.visibility === 'PRIVATE'">
        <v-alert type="info" tile>{{$t('events.race.private-results-msg')}}</v-alert>      
      </div>
      <div v-else-if="race.external_url" class="ma-4">
        <v-btn x-large :color="event.color || 'primary'" :class="event.color && $helpers.isDarkColor(event.color) ? 'white--text' : 'black--text'" primary :href="race.external_url" :target="race.external_url.indexOf('blank') ? '_blank' : null">{{$t('results.view-results')}}</v-btn>
      </div>
      <div v-else>
        <RaceResultsGrid :event="event" :race="race" :results="results" :meta="meta" :badges="badges" :filter="resultFilter" @search="updateSearch" @detailsClose="updateMetaTags"/>
      </div>
      <v-pagination 
        v-if="meta && meta.total_count > 50/* && meta.has_more (note: this is never true for paged results!) */ && !hasFilter && !subResults" 
        v-model="resultPage" 
        circle 
        :length="Math.ceil(meta.total_count / 50)" 
        @input="loadPage"
        />
      <div v-if="meta && meta.has_more && hasFilter" class="full-width-separator text-center mt-6">
        <v-btn class="" rounded outlined @click="loadNextPage">{{$t('shared.load-more-data')}}</v-btn>
      </div>
      
      <div v-if="race.scoring === 'TRAINING_PLAN'">
        <v-card-title class="sutitle">{{$t('events.race.training-plan-progress')}}</v-card-title>
        <v-card-text>{{$t('events.race.training-plan-description')}}</v-card-text>
        <TrainingPlanProgress v-if="trainingPlan" :event="event" :race="race" :training-plan="trainingPlan" :meta="meta" :badges="badges" />
      </div>
      <div v-else-if="badgesForDisplay && badgesForDisplay.length > 0 && !isWorkoutVideoChallenge">
        <v-card-title class="sutitle">{{$t('events.race.badges')}}</v-card-title>
        <v-card-text>{{$t('events.race.badges-description')}}</v-card-text>
        <RaceBadgesGrid :event="event" :race="race" :badges="badgesForDisplay" @click="badgeSelected" />
        <v-divider></v-divider>
      </div>

      <v-divider></v-divider>
      <v-container v-if="!isEmbedded">
        <v-row>
          <v-col cols="12" sm="6" md="8" class="pl-0">
            <v-card-title class="subtitle">{{$t('events.rules.leaderboard')}}</v-card-title>
            <v-card-text>
              <vue-markdown v-if="race.scoring_desc" class="markdown" :html="false">{{race.scoring_desc }}</vue-markdown>
              <div v-else>
                <p>
                  <v-icon small>{{ $helpers.getScoringIcon(race.scoring) }}</v-icon> {{ $t('events.rules.scoring-method', {method: $t('events.rules.scoring-methods.'+ race.scoring) }) }} 
                  <span v-if="race.goal_desc">{{race.goal_desc}}</span>
                  <span v-else>
                    {{ $t('events.rules.goal-is', {goal: $helpers.getGoalDistanceForDisplay($options, race, race.dist, event)}) }}
                  </span>
                </p>
                <p v-if="race.min_elevation && race.min_elevation > 0"><v-icon small>fa-mountain</v-icon> {{$t('events.rules.minimum-elevation', { min_elevation : $options.filters.elevation(race.min_elevation, event.unit)})}}</p>
                <p v-if="race.max_dist && race.max_dist > 0"><v-icon small>fa-ruler</v-icon> {{$t('events.rules.distance', { min_dist : $options.filters.distance(race.min_dist, event.unit), max_dist : $options.filters.distance(race.max_dist, event.unit) })}}</p>
                <p v-if="race.geofence">
                  <v-icon small>fa-draw-polygon</v-icon> {{$t('events.rules.geofence')}}
                  <RaceGeoFenceDialog :event="event" :race="race"/>
                </p>
              </div>
            </v-card-text>
          </v-col>
          <v-col v-if="event.type !== 'HYBRID' && event.type !== 'RESULTS'" cols="12" sm="6" md="4">
            <EventJoinCard :event="event"/>
          </v-col>
        </v-row>
      </v-container>
      <RaceResultDetailsDialog ref="resultPopup" :event="event" :race="race" :badges="badges" @close="updateMetaTags"/>
      <BadgeDetailsDialog :event="event" :race="race" ref="badgeDetails" />
      <v-dialog v-if="highlightedBadge" v-model="highlightedBadge" width="600">
        <v-card>
          <v-card-title>
            {{ highlightedBadge.name }}
            <v-spacer/>
            <ShareButton color="primary" outlined/>
          </v-card-title>
          <v-card-text style="text-align:center;">
            <img :src="highlightedBadge.img" style="max-width: 100%; max-height:300px;"/>
          </v-card-text>
          <BadgeDetailsDialog :event="event" :race="race" :badgeId="highlightedBadge.id" :detailsType="highlightedBadge.details_type" />
        </v-card>
      </v-dialog>
      <v-dialog v-model="showShareCompleted" width="600">
        <v-card>
          <v-card-title>
            {{ event.name }}
            <v-spacer/>
            <ShareButton color="primary" outlined/>
          </v-card-title>
          <img :src="event.img" style="max-width: 100%;"/>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showShareCompleted = false">{{$t('shared.close')}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showShareCertificate" width="600">
        <v-card>
          <v-card-title>
            {{ event.name }}
            <v-spacer/>
            <ShareButton color="primary" outlined/>
          </v-card-title>
          <img :src="certificateImageUrl" style="max-width: 100%;"/>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showShareCertificate = false">{{$t('shared.close')}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { EventBus } from '@/plugins/eventbus.js';

import assetsService from "@/services/assetsService";
import eventService from "@/services/eventService";
import VueMarkdown from '@/components/VueMarkdown.vue'
import EventHeader from "@/components/EventHeader.vue";
import RaceHistogram from "@/components/RaceHistogram.vue";
import RaceHistogramByDate from "@/components/RaceHistogramByDate.vue";
import RaceResultsGrid from "@/components/RaceResultsGrid.vue";
import RaceResultsMapOptions from "@/components/RaceResultsMapOptions.vue";
import RaceResultsMap from "@/components/RaceResultsMap.vue";
import RaceResultsTileMap from "@/components/RaceResultsTileMap.vue";
import RaceResultsVideos from "@/components/RaceResultsVideos.vue";
import RaceResultFilter from "@/components/RaceResultFilter.vue";
import RaceResultDetailsDialog from "@/components/RaceResultDetailsDialog";
import RaceBadgesGrid from "@/components/RaceBadgesGrid";
import EventJoinCard from '@/components/EventJoinCard.vue';
import siteData from '@/data/site.json'
import ShareButton from "@/components/ShareButton";
import BadgeDetailsDialog from "@/components/BadgeDetailsDialog";
import RaceGeoFenceDialog from "@/components/RaceGeoFenceDialog";
import EventFriendReferral from "@/components/EventFriendReferral";
import TrainingPlanProgress from "@/components/TrainingPlanProgress";
import tenants from '@/data/tenants.config'
const tenant = tenants.current();

export default {
  name: "Race",
  components: {
    VueMarkdown,
    EventJoinCard,
    EventHeader,
    EventFriendReferral,
    RaceHistogram,
    RaceHistogramByDate,
    RaceResultsGrid,
    RaceResultsTileMap,
    RaceResultsVideos,
    RaceResultsMap,
    RaceResultsMapOptions,
    RaceResultFilter,
    RaceResultDetailsDialog,
    BadgeDetailsDialog,
    RaceBadgesGrid,
    RaceGeoFenceDialog,
    TrainingPlanProgress,
    ShareButton,
  },
  props: {
  },
  data() {
    return {
      eventId: null,
      raceId: null,
      event: null,
      races: null,
      race: null,
      meta: null,
      markers: null,
      badges: null,
      tiles: null,
      trainingPlan: null,
      highlightedBadge: null,
      results: null,
      resultFilter: null,
      resultPage: 1,
      subResults: null,
      showPointInfo: false,
      showShareCompleted: false,
      showShareCertificate: false,
      certificateImageUrl: null,
      scoringMethods: siteData.scoring_methods,
    };
  },
  async beforeRouteUpdate (to, from, next) {
    // react to route changes...
    this.badges = null;
    this.$refs.resultMap.clear();
    await this.loadRace(to.params.id, to.params.raceId);
    console.log('TO', to.params.raceId, this.race, this.breadcrumbItems);
    next();
  },
  async mounted() {
    await this.loadData();

    EventBus.$on('tilemap-highlight-tile', async tile => {
      if (!tile) {
        this.loadSubResults(null);
      }
    });
    
  },
  methods: {
    async loadData() {
      await this.loadRace(this.$route.params.id, this.$route.params.raceId);
    },
    async loadRace(eventId, raceId) {
      this.eventId = eventId;
      this.raceId = raceId;
//      this.event = this.event || (await eventService.get(this.eventId)).data;
      if (!this.event) {
        var eventResponse;
        try {
          eventResponse = await eventService.get(this.eventId, this.viewOptions);
        }
        catch (ex) {
          console.log('/// ex', ex, 'status:', ex.response.status, 'data', ex.response.data)
          if (ex.response && ex.response.status === 404) {
            var responseData = ex.response.data;
            if (responseData && responseData.redirect_url){
              // tenant switch
              window.location.href = responseData.redirect_url + '/' + this.$route.params.raceId;
            }
          }
          return;
        }
        this.event = eventResponse.data;
      }
      this.races = this.event.races;
      this.race = this.event.races.find(x => x.id === this.raceId);
      console.log('Loading state', this.event, this.race);
      if (this.$route.query.q || this.$route.query.category || this.$route.query.gender || this.$route.query.limit) {
        console.log('Loading query:', this.$route.query);
        if (this.resultFilter == null) {
          this.resultFilter = { search: this.$route.query.q, category: this.$route.query.category, gender: this.$route.query.gender, limit: this.$route.query.limit };
        }
      }

      const response = (await eventService.getRaceResults(this.eventId, this.raceId, this.resultFilter, this.resultPage, null, this.viewOptions)).data;
      this.results = response.data || [];
      this.meta = response.meta;
      if (this.race.route) {
        if (!this.hasFilter) {
          const markerResponse = (await eventService.getRaceMarkers(this.eventId, this.raceId, this.viewOptions)).data;
          this.markers = markerResponse.data || [];
        }
        //this.markers = [];
        if (this.markers && this.markers.length > 0) {
          this.$refs.resultMap.loadMarkers(this.markers, this.meta);
        }
        else {
          this.$refs.resultMap.loadResults(this.results, this.meta);
        }
      }
      var meta = null;
      if (this.badges == null) {
        this.badges = (await eventService.getRaceBadges(this.eventId, this.raceId, this.viewOptions)).data.data;
        if (this.race.route) {
          this.$refs.resultMap.ensureBadgesLoaded(this.badges);
        }
        var badgeId = this.$route.query.badge;
        this.highlightedBadge = this.badges.find(x => x.id == badgeId);
        //console.log('Sharing data for badge', highlightedBadge);
        if (this.highlightedBadge) {
          meta = {
            title: `I earned the badge ${this.highlightedBadge.name} at the ${this.event.name}`,
            description: `I earned the badge ${this.highlightedBadge.name} at the ${this.event.name} on ${tenant.title}`,
            image: this.highlightedBadge.img,
          }; 
        }
      }
      if (this.$route.query.share === 'completed') {
        this.showShareCompleted = true;
        meta = {
          title: `I completed the ${this.race.name} at the ${this.event.name}`,
          description: `I completed the ${this.race.name} at the ${this.event.name} on ${tenant.title}`,
          image: this.event.img,
        }; 
      }
      if (this.$route.query.share === 'certificate') {
        this.showShareCertificate = true;
        this.certificateImageUrl = this.$route.query.img;
        meta = {
          title: `I received my finisher certificate for the ${this.race.name} at the ${this.event.name}`,
          description: `I completed the ${this.race.name} at the ${this.event.name} on ${tenant.title}`,
          image: this.certificateImageUrl,
        }; 
      }

      this.updateMetaTags(meta);

      if (this.race.scoring === 'TRAINING_PLAN') {
        this.meta.badges = this.badges.map(x => ({ badge_id: x.id, total_count: x.count, first_reward_date: x.first_reward_date })); 
        this.trainingPlan = (await assetsService.getTrainingPlan(this.race.id)).data;
      }
      if (this.race.scoring === 'TILES') {
        this.tiles = (await eventService.getRaceTiles(this.eventId, this.raceId)).data.data;
        await this.$nextTick();
        this.$refs.tileResults.loadResults(this.tiles);
      }
      if (this.$route.query.tileId) {
        this.$refs.tileResults.loadTileResultsById(this.$route.query.tileId);
      }
    },

    async loadSubResults(subResults) {
      console.log('loadSubResults', subResults);
      this.closeResultPopup();
      if (subResults == null) {
        this.$refs.tileResults.clearSelection();
        // todo: not working, causing refresh loop
        //if ( && this.$route.query.tileId)
        //this.$router.replace({path: this.$route.path, query: { ...this.$route.query, tileId: null }})
      }
      if (subResults && subResults.tile.id !== this.$route.query.tileId) {
        // todo: not working, causing refresh loop
        // this.$router.replace({path: this.$route.path, query: { ...this.$route.query, tileId: subResults.tile.id }})
      }
      this.subResults = subResults;
      //const query = { ...this.$route.query, tileId: subResults.tile.id };
      //this.$router.push({ query });
    },

    async updateMetaTags(meta) {
      meta = meta || {
        title: this.race.name + ' at the ' + this.event.name + ' on ' + tenant.name, 
        description: this.event.description,
        image: this.event.img,
      }; 
      EventBus.$emit('page-header-change', meta);
    },

    async badgeSelected(badge) {
      if (badge.first_reward_date) {
        this.highlightedBadge = this.badges.find(x => x.id == badge.id);
        this.$nextTick(() => {
          this.$refs.badgeDialog.open(badge);
        });
      }
    },

    async updateSearch(query) {
      this.$refs.filter.loadFilter({search: query });
      this.$refs.filter.search();
    },

    async updateResults(filter) {
      this.resultPage = 1;
      this.resultFilter = filter;
      if (this.hasFilter) {
        this.markers = [];
      }
      await this.loadRace(this.eventId, this.raceId);
//      this.loadData();
      this.$analytics.event('search', {
        'event_category': 'results',
        'event_label': this.eventId,
      });
    },

    /*async search() {
      this.resultPage = 1;
      const response = (await eventService.getRaceResults(this.eventId, this.raceId, { search: this.resultSearch})).data;
      this.results = response.data;
      this.meta = response.meta;
      if (this.race.route) {
        if (this.resultSearch || this.markers.length == 0) {
          this.$refs.resultMap.loadResults(this.results, this.meta);
        }
        else {
          this.$refs.resultMap.loadMarkers(this.markers, this.meta);
        }
      }
      this.$analytics.event('search', {
        'event_category': 'results',
        'event_label': this.eventId,
      });
    },*/
    
    async openResultPopup(id) {
      console.log ('opening popup', id);
      await this.$refs.resultPopup.open(id);
    },
    async closeResultPopup() {
      await this.$refs.resultPopup.close();
      this.updateMetaTags();
    },

    async openBadgeDetails(badge) {
      await this.$refs.badgeDetails.open(badge);
    },

    async loadNextPage() {
//      await this.loadPage(this.resultPage == null ? 2 : this.resultPage+1);
      console.log('/// next page', this.resultFilter, this.meta);
      const formData = {
        continuationToken: this.meta && this.meta.continuation_token,
      }
      const response = (await eventService.getRaceResults(this.eventId, this.raceId, this.resultFilter, null, formData)).data;
      this.results = response.data;
      this.meta = response.meta;
    },
    
    async loadPage(page) {
      this.resultPage = page;
      const response = (await eventService.getRaceResults(this.eventId, this.raceId, null, this.resultPage)).data;
      this.results = response.data;
      this.meta = response.meta;
      console.log('Page loaded', page, this.meta);
      this.$analytics.event('search', {
        'event_category': 'results',
        'event_label': this.eventId,
      });

      /*if (this.race.route) {
        this.$refs.resultMap.loadNewData(this.results);
      }*/
    },

  },
  watch: {
  },
  computed: {
    ...mapGetters({
      user: "user"
    }),
    
    breadcrumbItems() {
      return !this.event || !this.race ? [] : [
        { text: this.event.name, exact: true, to: { name: 'event', params: {id: this.event.id}} },
        { text: this.race.name, disabled: true },
      ];
    },
    badgesForDisplay() {
      return this.badges && this.badges.filter(x => x.metric !== 'SPLIT' && x.metric !== 'AGGREGATION');
    },
    isWorkoutVideoChallenge() {
      return this.race && this.race.badge_scoring === 'WORKOUT_VIDEOS';
    },
    isEmbedded() {
      return this.$route.query.view === 'embed' || this.$store.state.view == 'embed';
    },
    viewOptions() {
      let obj = {
        mockAlgo: this.$route.query.mock,
        allowDraft: this.$route.query.draft
      };
      return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));
    },
    hasFilter() {
      return this.resultFilter 
          && (this.resultFilter.search
           || this.resultFilter.gender
           || this.resultFilter.category 
           || (this.resultFilter.onlyOfficialResults && this.resultFilter.onlyOfficialResults !== '*'));
    },
    scoreByDayLabel() {
      if (this.race && this.race.scoring == 'STEPS') {
        return this.event.result_format === 'POINTS' ? this.$t('results.details.score-by-day-points') : this.$t('results.details.score-by-day-steps');
      }
      else if (this.race && this.race.scoring == 'DISTANCE') {
        return this.$t('results.details.score-by-day-distance');
      }
      return this.$t('results.details.score-by-day');
    },
  },

};
</script>
<style lang="scss">

.result--filter { position: sticky; top: 59px; z-index:4; background-color: white; }
.view--embedded .result--filter { top: 0px;  }

</style>

